import { useEffect, useRef } from 'react';
import gsap from 'gsap';

/**
 * Simple SVG sparkline component for inline metric visualization.
 *
 * @param {number[]} data - Array of numeric values
 * @param {string} color - Line color (default: #66ffb7)
 * @param {number} width - SVG width (default: 100)
 * @param {number} height - SVG height (default: 24)
 * @param {boolean} animate - Whether to animate the line drawing (default: true)
 */
export default function Sparkline({
  data = [],
  color = '#66ffb7',
  width = 100,
  height = 24,
  animate = true,
  strokeWidth = 1.5,
}) {
  const pathRef = useRef(null);

  // Generate SVG path from data
  const getPath = () => {
    if (!data || data.length < 2) return '';

    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;

    const points = data.map((value, index) => {
      const x = (index / (data.length - 1)) * width;
      const y = height - ((value - min) / range) * (height - 4) - 2;
      return `${x},${y}`;
    });

    return `M ${points.join(' L ')}`;
  };

  // Animate path drawing
  useEffect(() => {
    if (animate && pathRef.current && data.length >= 2) {
      const path = pathRef.current;
      const length = path.getTotalLength();

      gsap.set(path, {
        strokeDasharray: length,
        strokeDashoffset: length,
      });

      gsap.to(path, {
        strokeDashoffset: 0,
        duration: 0.6,
        ease: 'power2.out',
      });
    }
  }, [data, animate]);

  if (!data || data.length < 2) {
    return (
      <svg width={width} height={height} className="sparkline">
        <line
          x1="0"
          y1={height / 2}
          x2={width}
          y2={height / 2}
          stroke="var(--muted)"
          strokeWidth="1"
          strokeDasharray="4,4"
        />
      </svg>
    );
  }

  return (
    <svg width={width} height={height} className="sparkline">
      <path
        ref={pathRef}
        d={getPath()}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
